/* eslint-disable global-require */
import { Settings } from 'luxon';
import { registerLocale, setDefaultLocale } from 'react-datepicker';

import { APIConfig } from 'src/config';
import logger from 'src/util/logger';

import { isCaptureModeEnabled, wrapMessagesWithMarkers } from './capture';
import { getLocaleNumberFormat } from './constants';

/**
 * @constant
 * @type {string} DEFAULT_LOCALE The default locale for the application.
 * @default
 */
export const DEFAULT_LOCALE = 'en-AU';

/**
 * @constant
 * @type {Array<string>} SUPPORTED_DEFAULT_LOCALES The list of supported locales for the
 * application.
 * @default
 */
export const SUPPORTED_DEFAULT_LOCALES = Object.freeze([
  'en', 'en-AU', 'en-GB', 'en-US', 'it', 'ja', 'fr-FR',
]);

/**
 * @constant
 * @type {number} LOCALE_CATALOG_TIMEOUT_MS The timeout for fetching locale catalogs in
 * milliseconds.
 * @default
 */
const LOCALE_CATALOG_TIMEOUT_MS = 10000;

/**
 * @constant
 * @type {object} EMBEDDED_MESSAGES A static map of embedded i18n messages for supported
 * locales.
 * @default
 */
const EMBEDDED_MESSAGES = Object.freeze({
  en: require('../../../assets/i18n/compiled/en/en.json'),
  'en-AU': require('../../../assets/i18n/compiled/en-AU/en-AU.json'),
  'en-GB': require('../../../assets/i18n/compiled/en-GB/en-GB.json'),
  'en-US': require('../../../assets/i18n/compiled/en-US/en-US.json'),
  'fr-FR': require('../../../assets/i18n/compiled/fr-FR/fr-FR.json'),
  it: require('../../../assets/i18n/compiled/it/it.json'),
  ja: require('../../../assets/i18n/compiled/ja/ja.json'),
});

// Caches for loaded locale catalogs and promises to avoid redundant fetches.
const catalogCache = new Map();
// Caches for ongoing fetch promises to prevent duplicate requests for the same locale.
const catalogPromiseCache = new Map();
// Cache for the locale policy, which includes approved locales and any overrides.
let localePolicy;

/**
 * Returns true if the locale is one supported natively by Powertracer.
 * @param {string} locale
 * @returns {boolean} true if supported, false if not.
 */
const isSupported = (locale) => SUPPORTED_DEFAULT_LOCALES.includes(locale);

/**
 * Canonicalizes a locale string.
 * @param {string} locale The locale string to canonicalize.
 * @returns {string|null} The canonicalized locale string, or null if invalid.
 */
const canonicalizeLocale = (locale) => {
  if (!locale || typeof locale !== 'string') {
    return null;
  }

  try {
    const [canonicalLocale] = Intl.getCanonicalLocales(locale);
    return canonicalLocale || null;
  } catch (error) {
    return null;
  }
};

/**
 * Returns locale candidates from most to least specific.
 * @param {string} locale requested locale.
 * @returns {Array<string>} canonical full, language-region, and language candidates.
 */
const getLocaleFallbackCandidates = (locale) => {
  const canonicalLocale = canonicalizeLocale(locale);
  if (!canonicalLocale) {
    return [];
  }

  const localeData = new Intl.Locale(canonicalLocale);
  const languageRegion = localeData.region
    ? `${localeData.language}-${localeData.region}` : localeData.language;
  return [...new Set([canonicalLocale, languageRegion, localeData.language])];
};

/**
 * Returns the supported date-fns fallback for a locale.
 * @param {string} locale
 * @returns {string|null} the supported fallback locale, or null when unsupported.
 */
const getSupportedDefaultLocale = (locale) => {
  const supportedLocale = getLocaleFallbackCandidates(locale).find(isSupported);
  return supportedLocale || null;
};

/**
 * Normalizes a list of locale overrides from the API config.
 * @param {Array<string>} localeOverrides list of locale overrides.
 * @returns {Array<string>} normalized list of approved locales.
 */
const getNormalizedLocaleOverrides = (localeOverrides) => {
  if (localeOverrides === undefined || localeOverrides === null) {
    return Object.freeze([]);
  }
  if (!Array.isArray(localeOverrides)) {
    logger.warn('Locale policy entry ignored', { category: 'invalid-list' });
    return Object.freeze([]);
  }

  const normalizedLocales = new Set();
  localeOverrides.forEach((locale) => {
    const canonicalLocale = canonicalizeLocale(locale);
    if (!canonicalLocale) {
      logger.warn('Locale policy entry ignored', { category: 'invalid-entry' });
    } else if (!isSupported(canonicalLocale)) {
      normalizedLocales.add(canonicalLocale);
    }
  });

  return Object.freeze([...normalizedLocales]);
};

/**
 * Returns the locale policy, which includes the approved locales and any overrides.
 * @returns {{approvedLocales: Array<string>, localeOverrides: Array<string>}}
 *   the locale policy.
 */
const getLocalePolicy = () => {
  if (localePolicy) {
    return localePolicy;
  }
  const localeOverrides = getNormalizedLocaleOverrides(APIConfig()?.LOCALE_OVERRIDES);
  localePolicy = Object.freeze({
    approvedLocales: Object.freeze([...SUPPORTED_DEFAULT_LOCALES, ...localeOverrides]),
    localeOverrides,
  });
  return localePolicy;
};

/**
 * Resolves a canonical locale only when it is explicitly approved.
 * @param {string} fullLocale requested locale.
 * @returns {{defaultLocale: string, fullLocale: string, locale: string, overrideUrl: string|null}}
 *   resolved locale metadata.
 */
export const resolveLocale = (fullLocale) => {
  const canonicalLocale = canonicalizeLocale(fullLocale);
  const { approvedLocales, localeOverrides } = getLocalePolicy();
  if (!canonicalLocale || !approvedLocales.includes(canonicalLocale)) {
    throw new RangeError('Unsupported locale');
  }

  const defaultLocale = getSupportedDefaultLocale(canonicalLocale);
  if (!defaultLocale) {
    throw new RangeError('Unsupported locale');
  }

  const encodedLocale = encodeURIComponent(canonicalLocale);
  return Object.freeze({
    defaultLocale,
    fullLocale: canonicalLocale,
    locale: canonicalLocale,
    overrideUrl: localeOverrides.includes(canonicalLocale)
      ? `/public/i18n/${encodedLocale}/${encodedLocale}.json` : null,
  });
};

/**
 * Returns the resolved locale or null when unsupported.
 * @param {string} locale requested locale.
 * @returns {{defaultLocale: string, fullLocale: string, locale: string,
 *   overrideUrl: string|null}|null}
 *   resolved locale metadata or null when unsupported.
 */
const resolveLocaleOrNull = (locale) => {
  try {
    return resolveLocale(locale);
  } catch (error) {
    return null;
  }
};

/**
 * Resolves the first approved locale from the ordered fallback candidates.
 * @param {string} locale requested locale.
 * @returns {{defaultLocale: string, fullLocale: string, locale: string,
 *   overrideUrl: string|null}|null}
 *   resolved locale metadata or null when no candidate is approved.
 */
export const resolveLocaleFallbackOrNull = (locale) => {
  const candidates = getLocaleFallbackCandidates(locale);
  for (let index = 0; index < candidates.length; index += 1) {
    const resolution = resolveLocaleOrNull(candidates[index]);
    if (resolution) {
      return resolution;
    }
  }
  return null;
};

/**
 * Returns the configured locale resolution or falls back to the default.
 * @returns {{defaultLocale: string, fullLocale: string, locale: string, overrideUrl: string|null}}
 *   the configured locale resolution.
 */
const getConfiguredResolution = () => {
  const configuredLocale = APIConfig()?.LOCALE;
  const configuredResolution = resolveLocaleFallbackOrNull(configuredLocale);
  if (configuredLocale && !configuredResolution) {
    logger.warn('Locale bootstrap fallback', { category: 'invalid-configured-locale' });
  }
  return configuredResolution || resolveLocale(DEFAULT_LOCALE);
};

/**
 * Returns the bootstrap locale resolution, considering the URL and configured locales.
 * @returns {{defaultLocale: string, fullLocale: string, locale: string, overrideUrl: string|null}}
 *   the bootstrap locale resolution.
 */
const getBootstrapResolution = () => {
  const requestLocale = new URLSearchParams(window.location.search).get('locale');
  const requestResolution = resolveLocaleFallbackOrNull(requestLocale);
  if (requestResolution) {
    return requestResolution;
  }

  if (requestLocale) {
    logger.warn('Locale bootstrap fallback', { category: 'invalid-url-locale' });
    return resolveLocale(DEFAULT_LOCALE);
  }
  return getConfiguredResolution();
};

/**
 * Returns whether an application locale has a supported fallback locale.
 * @param {string} locale locale, including an optional private-use tag.
 * @returns {boolean} true when the locale is supported.
 */
export const isSupportedLocale = (locale) => Boolean(getSupportedDefaultLocale(locale));

/**
 * Returns the default locale for the selected locale.
 * @param {string} [locale] a valid locale as per
 * @returns {string} the stripped locale without any partner specifics.
 */
export const getDefaultLocale = (locale) => (
  getSupportedDefaultLocale(locale)
  || getSupportedDefaultLocale(typeof locale === 'string' ? locale.split('-x-')[0] : locale)
  || DEFAULT_LOCALE
);

/**
 * Returns the language for the locale.
 * @param {string} locale
 * @returns {string} the languange.
 */
export const getLanguage = (locale) => {
  if (!locale || !(typeof locale === 'string')) {
    const [language] = DEFAULT_LOCALE.split('-');

    return language;
  }

  const [language] = locale.split('-');

  return language;
};

/**
 * Trimmed locale
 * @param {string} locale
 * @returns {string} The trimmed locale.
 */
export const trimLocale = (locale) => {
  if (typeof locale !== 'string') {
    return locale;
  }
  const matched = locale.match(/^([a-z]{2}(-[A-Z]{2})?)-x-(.+?)$/);
  if (!matched) {
    return locale;
  }

  const privateTag = matched[3].replace(/[^a-zA-Z]/, '').toLowerCase().substring(0, 8);
  const trimmed = `${matched[1]}-x-${privateTag}`;

  return trimmed;
};

/**
 * Returns the locale of the application based on the partner/retailer
 * @returns {string} - locale
 */
export const getFullLocale = () => getBootstrapResolution().fullLocale;

/**
 * Returns the approved configured partner locale without considering the URL.
 * @returns {string} configured locale or the safe default.
 */
export const getConfiguredFullLocale = () => (
  getConfiguredResolution().fullLocale
);

/**
 * Returns the locale of the application based on the partner/retailer
 * @returns {string} - locale
 */
export const getLocale = () => getFullLocale();

/**
 * Return an embedded set of i18n messages.
 * @param {string} fullLocale the full locale of the embedded langauge pack.
 * @returns {object} the i18n messages.
 */
export const i18nMessagesEmbedded = (fullLocale) => EMBEDDED_MESSAGES[fullLocale] || {};

/**
 * Builds a catalog error with the specified category.
 * @param {string} category The category of the error.
 * @returns {Error} The built error object.
 */
const buildCatalogError = (category) => {
  const error = new Error('Locale catalog unavailable');
  error.category = category;
  return error;
};

/**
 * Fetches the locale catalog from the specified URL.
 * @param {{overrideUrl: string}} param0 The parameters for the fetch request.
 * @returns {Promise<object>} The fetched locale messages.
 */
const getRemoteCatalog = async ({ overrideUrl }) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), LOCALE_CATALOG_TIMEOUT_MS);
  try {
    const response = await fetch(overrideUrl, {
      redirect: 'error',
      signal: controller.signal,
    });
    if (!response.ok) {
      throw buildCatalogError('status');
    }

    let messages;
    try {
      messages = await response.json();
    } catch (error) {
      if (error?.name === 'AbortError') {
        throw error;
      }
      throw buildCatalogError('parse');
    }

    const validMessages = messages && typeof messages === 'object'
      && !Array.isArray(messages)
      && Object.values(messages).every((message) => typeof message === 'string');
    if (!validMessages) {
      throw buildCatalogError('shape');
    }
    return messages;
  } finally {
    clearTimeout(timeout);
  }
};

/**
 * Returns the category of a catalog failure based on the error.
 * @param {Error} error The error object.
 * @returns {string} The failure category.
 */
const getCatalogFailureCategory = (error) => {
  if (error?.category) {
    return error.category;
  }
  return error?.name === 'AbortError' ? 'timeout' : 'request';
};

/**
 * Loads the locale catalog override for the specified resolution.
 * @param {{overrideUrl: string}} resolution The locale resolution.
 * @returns {Promise<object>} The loaded locale messages.
 */
const loadCatalogOverride = (resolution) => {
  if (!resolution.overrideUrl) {
    return Promise.resolve({});
  }
  if (catalogCache.has(resolution.fullLocale)) {
    return Promise.resolve(catalogCache.get(resolution.fullLocale));
  }
  if (catalogPromiseCache.has(resolution.fullLocale)) {
    return catalogPromiseCache.get(resolution.fullLocale);
  }

  const catalogPromise = getRemoteCatalog(resolution)
    .then((messages) => {
      catalogCache.set(resolution.fullLocale, messages);
      return messages;
    })
    .catch((error) => {
      logger.warn('Locale catalog fallback', {
        category: getCatalogFailureCategory(error),
        locale: resolution.fullLocale,
      });
      return {};
    })
    .finally(() => catalogPromiseCache.delete(resolution.fullLocale));
  catalogPromiseCache.set(resolution.fullLocale, catalogPromise);
  return catalogPromise;
};

/**
 * Clears the cached locale catalogs and resets the locale policy.
 * This is useful for testing or when the locale configuration changes.
 * @returns {void}
 */
export const clearI18nCatalogCache = () => {
  catalogCache.clear();
  catalogPromiseCache.clear();
  localePolicy = undefined;
};

/**
 * Returns a static map of supported locales for date-fns v3 (no index.js in date-fns/locale/).
 * Dynamic require(`date-fns/locale/${x}`) fails webpack resolution in v3.
 */
export const DATE_FNS_LOCALES = {
  en: require('date-fns/locale/en-US'),
  'en-AU': require('date-fns/locale/en-AU'),
  'en-GB': require('date-fns/locale/en-GB'),
  'en-US': require('date-fns/locale/en-US'),
  it: require('date-fns/locale/it'),
  ja: require('date-fns/locale/ja'),
  'fr-FR': require('date-fns/locale/fr'),
};

/**
 * Returns the date-fns locale for the specified default locale.
 * @param {string} defaultLocale The default locale identifier.
 * @returns {object} The date-fns locale object.
 */
const getDateFnsLocale = (defaultLocale) => {
  const localeModule = DATE_FNS_LOCALES[defaultLocale] || DATE_FNS_LOCALES[DEFAULT_LOCALE];
  return Object.values(localeModule)[0];
};

/**
 * Registers the date-fns locale under its embedded and full locale identifiers.
 * @param {string} defaultLocale embedded locale identifier.
 * @param {string} fullLocale active full locale identifier.
 * @returns {void}
 */
const registerDateFnsLocales = (defaultLocale, fullLocale) => {
  const dateFnsLocale = getDateFnsLocale(defaultLocale);
  registerLocale(defaultLocale, dateFnsLocale);
  registerLocale(fullLocale, dateFnsLocale);
};

/**
 * Appends translation key markers when the build is in i18n capture mode.
 * @param {object} messages compiled messages keyed by id.
 * @returns {object} messages, wrapped only in capture mode.
 */
const getMessagesWithCaptureMarkers = (messages) => (
  isCaptureModeEnabled() ? wrapMessagesWithMarkers(messages) : messages
);

/**
 * Builds the complete configuration for a supported application locale.
 * @param {object|string} resolution approved locale metadata or a locale identifier.
 * @param {object} [messages] already-loaded messages.
 * @returns {{fullLocale: string, locale: string, defaultLocale: string, messages: object}}
 *   the resolved configuration.
 */
export const buildI18nConfig = (resolution, messages) => {
  const resolvedLocale = typeof resolution === 'string'
    ? resolveLocale(resolution) : resolution;
  if (!resolvedLocale) {
    throw new RangeError('Unsupported locale');
  }
  return {
    defaultLocale: resolvedLocale.defaultLocale,
    fullLocale: resolvedLocale.fullLocale,
    locale: resolvedLocale.locale,
    messages: getMessagesWithCaptureMarkers(
      messages || i18nMessagesEmbedded(resolvedLocale.defaultLocale),
    ),
  };
};

/**
 * Loads a complete i18n configuration for an approved locale.
 * @param {string} fullLocale requested locale.
 * @returns {Promise<object>} complete i18n configuration.
 */
export const loadI18nConfig = async (fullLocale) => {
  const resolution = resolveLocale(fullLocale);
  const messagesDefaultLocale = i18nMessagesEmbedded(resolution.defaultLocale);
  const messagesOverride = await loadCatalogOverride(resolution);
  return buildI18nConfig(resolution, { ...messagesDefaultLocale, ...messagesOverride });
};

/**
 * Applies a locale configuration to non-React date/time libraries.
 * @param {{locale: string, defaultLocale: string}} config the active i18n configuration.
 * @returns {void}
 */
export const applyI18nConfig = ({ locale, defaultLocale }) => {
  registerDateFnsLocales(defaultLocale, locale);
  setDefaultLocale(locale);
  Settings.defaultLocale = locale;
};

/**
 * Resolves and applies the bootstrap locale configuration.
 * @returns {{fullLocale: string, locale: string, defaultLocale: string, messages: object}}
 *   the active configuration.
 */
export const initializeI18n = async () => {
  const resolution = getBootstrapResolution();
  const config = await loadI18nConfig(resolution.fullLocale);
  applyI18nConfig(config);
  return config;
};

/**
 * Retuns the language pack(static) that is used across the application
 * @returns {object} - language pack map
 */
export const i18nMessages = () => {
  let resolution;
  try {
    resolution = getBootstrapResolution();
  } catch (error) {
    resolution = { defaultLocale: DEFAULT_LOCALE, fullLocale: DEFAULT_LOCALE };
  }
  registerDateFnsLocales(resolution.defaultLocale, resolution.fullLocale);
  return getMessagesWithCaptureMarkers(i18nMessagesEmbedded(resolution.defaultLocale));
};

/**
 * Decimal formatting - if there is a separator defined in the decimal separator it
 * will use that or will use the default (.)
 * @param {string} value
 * @param {string} [locale] active presentation locale.
 * @returns {string} - decimal separated value
 */
export const i18nDecimalFormat = (value, locale) => {
  const { decimal } = getLocaleNumberFormat(locale || getLocale());
  return value.replace('.', decimal);
};
